import { useEffect, type MouseEvent, type ReactNode } from 'react';
import { CLOUDSCAPE_MOBILE_BREAKPOINT } from '../../utils/constants';

export interface AppShellProps {
    navigation: ReactNode;
    navigationOpen: boolean;
    onNavigationChange: (open: boolean) => void;
    children: ReactNode;
    footer?: ReactNode;
    /** Centered mark in the mobile toolbar. The live app passes `MobileNavLogo`. */
    mobileLogo?: ReactNode;
}

const isMobileViewport = () =>
    typeof window !== 'undefined' &&
    window.matchMedia(`(max-width: ${CLOUDSCAPE_MOBILE_BREAKPOINT}px)`).matches;

/** Cloudscape `menu` icon (size normal): 16px, 2px stroke. */
const MenuIcon = () => (
    <svg viewBox="0 0 16 16" width="16" height="16" xmlns="http://www.w3.org/2000/svg" focusable="false" aria-hidden="true">
        <path d="M15 3H1M15 8H1M15 13H1" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    </svg>
);

/** Cloudscape `close` icon (size normal): 16px, 2px stroke. */
const DrawerCloseIcon = () => (
    <svg viewBox="0 0 16 16" width="16" height="16" xmlns="http://www.w3.org/2000/svg" focusable="false" aria-hidden="true">
        <path d="m2 1.71 12 12M2 13.71l12-12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    </svg>
);

const CloseIcon = () => (
    <svg viewBox="0 0 16 16" width="16" height="16" xmlns="http://www.w3.org/2000/svg" focusable="false" aria-hidden="true">
        <path d="M11 2 5 8l6 6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

/**
 * Logged-in frame: navigation drawer, scrolling content, mobile toolbar, footer.
 * Replaces Cloudscape AppLayout. See APPSHELL.md.
 */
export const AppShell = ({
    navigation,
    navigationOpen,
    onNavigationChange,
    children,
    footer,
    mobileLogo,
}: AppShellProps) => {
    useEffect(() => {
        if (!navigationOpen) return;

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onNavigationChange(false);
        };

        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [navigationOpen, onNavigationChange]);

    const toggleNavigation = () => onNavigationChange(!navigationOpen);

    const handleNavClick = (event: MouseEvent<HTMLElement>) => {
        if (!isMobileViewport()) return;
        const anchor = (event.target as HTMLElement).closest('a');
        if (!anchor) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
        onNavigationChange(false);
    };

    const toggleLabel = navigationOpen ? 'Close navigation drawer' : 'Open navigation drawer';

    return (
        <div className={`app-shell${navigationOpen ? ' is-nav-open' : ''}`}>
            <div className="app-shell-body">
                <header className="app-shell-toolbar">
                    <button
                        type="button"
                        className="app-shell-nav-toggle"
                        aria-label="Open navigation drawer"
                        aria-expanded={navigationOpen}
                        onClick={toggleNavigation}
                    >
                        <MenuIcon />
                    </button>
                    {mobileLogo ? <div className="mobile-nav-logo-slot">{mobileLogo}</div> : null}
                </header>
                <div className={`app-shell-frame${navigationOpen ? ' is-nav-open' : ''}`}>
                    <button
                        type="button"
                        className="app-shell-nav-toggle app-shell-nav-toggle--edge"
                        aria-label={toggleLabel}
                        aria-expanded={navigationOpen}
                        onClick={toggleNavigation}
                    >
                        {navigationOpen ? <CloseIcon /> : <MenuIcon />}
                    </button>
                    <nav
                        className="app-shell-nav"
                        aria-label="Navigation drawer"
                        aria-hidden={!navigationOpen}
                        onClick={handleNavClick}
                    >
                        <button
                            type="button"
                            className="app-shell-nav-close"
                            aria-label="Close navigation drawer"
                            onClick={() => onNavigationChange(false)}
                        >
                            <DrawerCloseIcon />
                        </button>
                        {navigation}
                    </nav>
                    <main className="app-shell-content">{children}</main>
                </div>
            </div>
            {footer}
        </div>
    );
};
